import { values } from 'ramda';
import {
  DxfWriter, point3d, Colors, Units
} from '@tarikjabiri/dxf';
import PCNode from '../Nodes/PCNode';
import PCPipe from '../Pipes/PCPipe';
import PCSpool from '../Spools/PCSpool';

const NODE_LABEL_LAYER = 'Node_Labels';
const TEXT_HEIGHT = 1.0;
const TEXT_OFFSET_Y = 1.5;
const DEFAULT_PIPE_LAYER = 'Pipes';
const MAX_LAYER_NAME_LENGTH = 255;

const INVALID_LAYER_CHARS = /[<>/\\|:?*"=;]/g;
const HEX_COLOR_PATTERN = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const sanitizeDxfString = (value: string): string => value
  .split('')
  .map((char) => {
    const code = char.charCodeAt(0);
    return (code < 32 || code === 127) ? ' ' : char;
  })
  .join('')
  .replace(/\s+/g, ' ')
  .trim();

const sanitizeLayerName = (name: string): string => {
  const sanitized = sanitizeDxfString(name).replace(INVALID_LAYER_CHARS, '_');
  const fallback = sanitized || DEFAULT_PIPE_LAYER;
  return fallback.slice(0, MAX_LAYER_NAME_LENGTH);
};

const createUniqueLayerName = (baseLayerName: string, createdLayers: Set<string>): string => {
  if (!createdLayers.has(baseLayerName)) {
    createdLayers.add(baseLayerName);
    return baseLayerName;
  }

  for (let suffixIndex = 2; suffixIndex < Number.MAX_SAFE_INTEGER; suffixIndex += 1) {
    const suffix = `_${suffixIndex}`;
    const nameWithoutSuffix = baseLayerName.slice(0, MAX_LAYER_NAME_LENGTH - suffix.length);
    const candidate = `${nameWithoutSuffix}${suffix}`;
    if (!createdLayers.has(candidate)) {
      createdLayers.add(candidate);
      return candidate;
    }
  }

  createdLayers.add(baseLayerName);
  return baseLayerName;
};

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const hasFiniteCoords = (coords: { x: number; y: number; z: number } | undefined): boolean => !!coords
  && isFiniteNumber(coords.x)
  && isFiniteNumber(coords.y)
  && isFiniteNumber(coords.z);

const normaliseHexColour = (colour: string | undefined): string | undefined => {
  if (!colour) return undefined;
  const match = colour.trim().match(HEX_COLOR_PATTERN);
  if (!match) return undefined;

  const hex = match[1];
  if (hex.length === 3) {
    const expanded = hex
      .split('')
      .map((char) => `${char}${char}`)
      .join('');
    return `#${expanded.toUpperCase()}`;
  }

  return `#${hex.toUpperCase()}`;
};

const toDxfTrueColor = (colour: string | undefined): string | undefined => {
  const normalised = normaliseHexColour(colour);
  if (!normalised) return undefined;

  const hex = normalised.slice(1);
  const value = Number.parseInt(hex, 16);
  if (!Number.isFinite(value)) return undefined;
  return String(value);
};

const exportReticulationToDxf = (
  nodes: Record<string, PCNode>,
  spools: Record<string, PCSpool>,
  pipes: Record<string, PCPipe>,
): string => {
  const dxf = new DxfWriter();
  dxf.setUnits(Units.Meters);

  dxf.addLayer(NODE_LABEL_LAYER, Colors.White);

  const createdLayers = new Set<string>([NODE_LABEL_LAYER]);
  const layerByPipeId = new Map<string, string>();

  const getOrCreatePipeLayer = (pipe: PCPipe): string => {
    const existingLayer = layerByPipeId.get(pipe.pipeId);
    if (existingLayer) return existingLayer;

    const baseLayerName = sanitizeLayerName(pipe.name || pipe.pipeId || DEFAULT_PIPE_LAYER);
    const layerName = createUniqueLayerName(baseLayerName, createdLayers);
    dxf.addLayer(layerName, Colors.White);
    layerByPipeId.set(pipe.pipeId, layerName);
    return layerName;
  };

  values(pipes).forEach((pipe: PCPipe) => {
    getOrCreatePipeLayer(pipe);
  });

  values(spools).forEach((spool: PCSpool) => {
    if (!hasFiniteCoords(spool.pointA?.coords) || !hasFiniteCoords(spool.pointB?.coords)) return;

    const layerName = getOrCreatePipeLayer(spool.pipe);
    const start = point3d(spool.pointA.coords.x, spool.pointA.coords.y, spool.pointA.coords.z);
    const end = point3d(spool.pointB.coords.x, spool.pointB.coords.y, spool.pointB.coords.z);
    const trueColor = toDxfTrueColor(spool.pipe.colour);

    if (trueColor) {
      dxf.addLine(start, end, {
        layerName,
        trueColor,
      });
    } else {
      dxf.addLine(start, end, {
        layerName,
      });
    }
  });

  values(nodes).forEach((node: PCNode) => {
    if (!hasFiniteCoords(node.coords)) return;

    const label = sanitizeDxfString(node.id);
    if (!label) return;

    const textPosition = point3d(node.coords.x, node.coords.y + TEXT_OFFSET_Y, node.coords.z);

    dxf.addText(textPosition, TEXT_HEIGHT, label, {
      layerName: NODE_LABEL_LAYER,
    });
  });

  return dxf.stringify();
};

export default exportReticulationToDxf;
